import { ChangeDetectorRef, Component, inject, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import {
  BusinessPlanRequest,
  BusinessPlanResponse,
  CompanyInfo,
  PackageDetail,
} from '../../shared/interfaces/reserve-loading.model';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatOption, MatSelect } from '@angular/material/select';
import { ReserveLocationService } from '../../shared/services/reserve-location.service';
import {
  CheckLocationRequest,
  OrgDetail,
} from '../../shared/interfaces/reserve-location.model';
import { MatButton } from '@angular/material/button';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { BusinessReserveChooseGroupDialogComponent } from '../../shared/components/dialogs/business-reserve-choose-group-dialog/business-reserve-choose-group-dialog.component';
import { NumberCommaPipe } from '../../pipes/number-comma.pipe';
import { ReserveLoadingService } from '../../shared/services/reserve-loading.service';
import { BusinessAvailableDatesRequest } from '../../shared/interfaces/appointment-time.model';
import { AppointmentTimeService } from '../../shared/services/appointment-time.service';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';

/**
 * 企業方案結果頁面
 */
@Component({
  selector: 'app-business-reserve-result',
  standalone: true,
  imports: [
    TranslatePipe,
    MatSelect,
    FormsModule,
    MatOption,
    MatButton,
    NumberCommaPipe,
    MatFormField,
  ],
  templateUrl: './business-reserve-result.component.html',
  styleUrl: './business-reserve-result.component.scss',
})
export class BusinessReserveResultComponent implements OnInit, AfterViewInit, OnDestroy {
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  reserveLocationService: ReserveLocationService = inject(
    ReserveLocationService
  );
  reserveLoadingService: ReserveLoadingService = inject(ReserveLoadingService);
  appointmentTimeService: AppointmentTimeService = inject(AppointmentTimeService);
  translateService: TranslateService = inject(TranslateService);
  cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  router: Router = inject(Router);
  dialog: MatDialog = inject(MatDialog);

  businessPlanResponse?: BusinessPlanResponse; // 企業方案結果
  locations: Array<OrgDetail> = []; // 健檢中心列表
  selectedLocation: string = ''; // 選擇的健檢中心
  isLoadingPackages: boolean = false; // 新增 loading 狀態
  sponsorAmt: string = ''; // 企業補助金額

  @ViewChild('loadMoreTrigger') loadMoreTrigger?: ElementRef;
  
  visiblePackages: Array<PackageDetail> = [];
  private itemsPerPage = 9; // 每頁顯示9個方案
  private currentPage = 1;
  private observer?: IntersectionObserver;

  get hasMorePackages(): boolean {
    return this.visiblePackages.length < (this.packageDetailList?.length || 0);
  }

  ngOnInit(): void {
    // 將當前預約步驟設置為3
    setTimeout(() => {
      this.sharedStorageService.set('reserveStep', 3);
    });

    // 查詢健檢中心
    const locationRequest: CheckLocationRequest = {
      reservationType: this.sharedStorageService.get('reserveType') ?? '',
      companyNo: this.sharedStorageService.get('businessPlanRequest')?.companyNo ?? '',
    };
    this.reserveLocationService
      .checkLocations(locationRequest)
      .subscribe(res => {
        this.locations = res.data?.orgDetail ?? [];
        this.selectedLocation =
          this.sharedStorageService.get('userPlanRequest')?.orgNo ?? '';
          this.selectLocation();//初始化顯示該據點的方案
      });

    this.businessPlanResponse = this.sharedStorageService.get(
      'businessPlanResponse'
    );
  }

  ngAfterViewInit() {
    this.initializeVisiblePackages();
    setTimeout(() => this.setupIntersectionObserver(), 1000);
  }

  // 初始化可見的方案
  private initializeVisiblePackages() {
    const packages = this.packageDetailList || [];
    this.visiblePackages = packages.slice(0, this.itemsPerPage);

    // 重置分頁狀態
    this.currentPage = 1;
    
    // 手動觸發變更檢測
    this.cdr.detectChanges();

    // 重新設置 IntersectionObserver
    setTimeout(() => {
      if (this.observer) {
        this.observer.disconnect();
      }
      this.setupIntersectionObserver();
    }, 100);
  }

  // 展開更多方按卡牌(lazy loading)
  private setupIntersectionObserver() {
    if (!this.loadMoreTrigger) return;
    
    this.observer = new IntersectionObserver(
      (entries) => {
        // 檢查目標是否進入可視區域
        if (entries[0].isIntersecting && this.hasMorePackages) {
          this.loadMorePackages();
        }
      },
      { threshold: 0.1 }
    );
    
    // 監控觸發器元素
    this.observer.observe(this.loadMoreTrigger.nativeElement);
  }

  // 加載更多方案
  private loadMorePackages() {
    const packages = this.packageDetailList || [];
    const startIndex = this.currentPage * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const nextItems = packages.slice(startIndex, endIndex);
    
    this.visiblePackages = [...this.visiblePackages, ...nextItems]; // 合併新舊方案
    this.currentPage++;
    
    // 手動觸發變更檢測
    this.cdr.detectChanges();
  }

  // 組件銷毀時，停止觀察
  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  // 企業資訊
  get companyInfo(): CompanyInfo | undefined {
    return this.businessPlanResponse?.companyInfo;
  }

  // 格式化預約截止日
  get formattedDueDate(): string {
    const date = this.businessPlanResponse?.companyInfo.bookableDateE ?? '';
    return `${date?.slice(0, 4)}/${date?.slice(4, 6)}/${date?.slice(6, 8)}`;
  }

  // 方案列表
  get packageDetailList(): Array<PackageDetail> | undefined {
    return this.businessPlanResponse?.packageList;
  }

  // 選擇地點
  selectLocation() {
    this.isLoadingPackages = true;
    // 重置分頁狀態
    this.currentPage = 1;
    this.visiblePackages = [];
    if (this.observer) {
      this.observer.disconnect();
    }
    const userPlanRequest = this.sharedStorageService.get('userPlanRequest');
    if (userPlanRequest) {
      userPlanRequest.orgNo = this.selectedLocation;
      this.sharedStorageService.set('userPlanRequest', userPlanRequest);
      const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');

      // 更改據點時，重新渲染此頁面
      if (businessPlanRequest) {
        this.isLoadingPackages = true;
        // 重置分頁狀態
        this.currentPage = 1;
        this.visiblePackages = [];
        
        businessPlanRequest.orgNo = this.selectedLocation;

        const idLoginRequest = this.sharedStorageService.get('idLoginRequest');
        const createMemberRequest = this.sharedStorageService.get('createMemberRequest');
        const idNo = idLoginRequest?.idNo || createMemberRequest?.idNo || '';
        const bookingInfo = this.sharedStorageService.get('bookingInfo');

        // 如果身分證字號與員工編號相同，則將身分證字號存到 idNo 欄位，employeeId 保持乾淨只存員工編號
        if (idNo === businessPlanRequest?.employeeId) {
          console.log('將身分證與員編分開');
          businessPlanRequest.idNo = idNo;
          businessPlanRequest.employeeId = '';
        } else if (bookingInfo?.empId) {
          businessPlanRequest.employeeId = bookingInfo.empId;
        }
        console.log("企業產方案的businessPlanRequest", businessPlanRequest);
        this.sharedStorageService.set('businessPlanRequest', businessPlanRequest);



        // 呼叫 API 時，建立新物件，如果沒有 employeeId 則使用 idNo
        const apiRequest = {
          ...businessPlanRequest,
          employeeId: businessPlanRequest?.employeeId ?? '',
          idNo: userPlanRequest?.idNo ?? createMemberRequest?.idNo ?? idLoginRequest?.idNo ?? '',
        };

        this.reserveLoadingService
          .getBusinessPlanResult(apiRequest)
          .subscribe({
            next: (res) => {
              if (res.data && res.data.packageList) {
                this.sharedStorageService.set('businessPlanResponse', res.data);
                this.businessPlanResponse = res.data;

                this.sponsorAmt = this.businessPlanResponse?.companyInfo?.sponsorAmt ?? this.sharedStorageService.get('sponsorAmt') ?? '';
                
                // 如果是從企業預約修改檢項-重選方案，重設補助金為初始化值
                if (this.sharedStorageService.get('modifyType') === 'modifyExam') {
                  this.sharedStorageService.set('sponsorAmt', this.sponsorAmt);
                  console.log('來自會員中心企業預約修改檢項，重選方案將重新設置 sponsorAmt:', this.sharedStorageService.get('sponsorAmt'));
                }

                // 重新初始化可見方案
                this.initializeVisiblePackages();
                
                setTimeout(() => {
                  this.cdr.detectChanges();
                }, 0);
              }
              this.isLoadingPackages = false;
            },
            error: (error) => {
              console.error('載入方案失敗:', error);
              this.isLoadingPackages = false;
            }
          });
      }
    }
  }

  // 查看方案
  viewPackage(packageDetail: PackageDetail) {
    this.sharedStorageService.set('businessSelectedPackage', packageDetail);
    this.router.navigate(['/healthcheck/reserve/plancompare']).then();
  }

  // 選擇方案
  async selectPackage(packageDetail: PackageDetail) {
    // 從所有方案中過濾出選擇的方案，並copy一份新的做為選擇的方案
    const businessPlanResponse = this.sharedStorageService.get(
      'businessPlanResponse'
    );
    packageDetail = JSON.parse(
      JSON.stringify(
        businessPlanResponse?.packageList.filter(
          info => info.packageNo === packageDetail.packageNo
        )[0] ?? packageDetail
      )
    );
    this.sharedStorageService.set('businessSelectedPackage', packageDetail);

    const packageDetailIncludeGroup = [
      ...(packageDetail.groupChooseList ?? []),
      ...(packageDetail.examChooseList ?? []),
    ];

    console.log('packageDetailIncludeGroup:', packageDetailIncludeGroup);
    let totalGroupCount = 0; // 總共有多少組合

    totalGroupCount = packageDetailIncludeGroup.length;
    
    console.log('totalGroupCount:', totalGroupCount);

    if (totalGroupCount > 0) {
      if (totalGroupCount === 1) {
        // 單一組合，直接處理
        const chooseResult = await this.dialog
          .open(BusinessReserveChooseGroupDialogComponent, {
            data: { 
              group: packageDetailIncludeGroup[0],
              totalGroupCount: totalGroupCount,
            },
          })
          .afterClosed()
          .toPromise();
        
        if (chooseResult) {

          // 若是從會員中心 - 調整檢項來，需要重新設置originalPackagePrice，方便後續判斷 總金額 < 原套檢金額
          if (this.sharedStorageService.get('modifyType') === 'modifyExam') {
            // 計算選擇方案的原始價格
            const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
            if (businessSelectedPackage) {
              const originalPackagePrice = Number(businessSelectedPackage.price);
              
              console.log('沒檢項originalPackagePrice', originalPackagePrice);
              this.sharedStorageService.set('originalPackagePrice', originalPackagePrice);
              this.sharedStorageService.set('packagePrice', originalPackagePrice.toString());
            }
          }

          // 過濾selected的group
          const businessSelectedPackage = this.sharedStorageService.get(
            'businessSelectedPackage'
          );
          if (businessSelectedPackage) {
            // 處理 groupChooseList
            businessSelectedPackage.groupChooseList?.forEach(
              (groupChooseInfo: any) => {
                groupChooseInfo.groupList = groupChooseInfo.groupList?.filter(
                  (group: any) => group.selected
                );
              }
            );
            this.sharedStorageService.set(
              'businessSelectedPackage',
              businessSelectedPackage
            );
          }
        }
      } else {
        console.log("多個組合:", packageDetailIncludeGroup);
        // 多個組合，一次傳入所有組合
        const chooseResult = await this.dialog
          .open(BusinessReserveChooseGroupDialogComponent, {
            data: { 
              groups: packageDetailIncludeGroup,
              totalGroupCount: totalGroupCount,
            },
          })
          .afterClosed()
          .toPromise();
        
        if (chooseResult) {
          // 若是從會員中心 - 調整檢項來，需要重新設置originalPackagePrice，方便後續判斷 總金額 < 原套檢金額
          if (this.sharedStorageService.get('modifyType') === 'modifyExam') {
            // 計算選擇方案的原始價格
            const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
            if (businessSelectedPackage) {
              const originalPackagePrice = Number(businessSelectedPackage.price);
              
              console.log('沒檢項originalPackagePrice', originalPackagePrice);
              this.sharedStorageService.set('originalPackagePrice', originalPackagePrice);
               this.sharedStorageService.set('packagePrice', originalPackagePrice.toString());
            }
          }

          // 過濾selected的group
          const businessSelectedPackage = this.sharedStorageService.get(
            'businessSelectedPackage'
          );
          if (businessSelectedPackage) {
            // 處理 groupChooseList
            businessSelectedPackage.groupChooseList?.forEach(
              (groupChooseInfo: any) => {
                groupChooseInfo.groupList = groupChooseInfo.groupList?.filter(
                  (group: any) => group.selected
                );
              }
            );
            this.sharedStorageService.set(
              'businessSelectedPackage',
              businessSelectedPackage
            );
          }
        }
      }
    } 
    // 若沒有組合要選
    else {
      this.checkBusinessAvailableDates();

      // 若是從會員中心 - 調整檢項來，需要重新設置originalPackagePrice，方便後續判斷 總金額 < 原套檢金額
      if (this.sharedStorageService.get('modifyType') === 'modifyExam') {
        // 計算選擇方案的原始價格
        const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
        if (businessSelectedPackage) {
          const originalPackagePrice = Number(businessSelectedPackage.price);
          
          console.log('沒檢項originalPackagePrice', originalPackagePrice);
          this.sharedStorageService.set('originalPackagePrice', originalPackagePrice);
          this.sharedStorageService.set('packagePrice',originalPackagePrice.toString());
        }
      }
    }
  }
  // 建立簡化的檢項清單，只包含 examItem 和 examName
  private buildSimpleExamItemList(): { examItem: string; examName: string }[] {
    const examItemList: { examItem: string; examName: string }[] = [];
    const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
    
    if (!businessSelectedPackage) return examItemList;

    // 加入方案的 deptList 檢項
    businessSelectedPackage.deptList?.forEach((dept: any) => {
      dept.examItemList?.forEach((exam: any) => {
        examItemList.push({
          examItem: exam.examItem,
          examName: exam.examName
        });
      });
    });
    return examItemList;
  }

  // 建立完整的檢項清單並儲存到 sharedStorage
  private buildAndSaveExamItemList(): void {
    const examItemList: any[] = [];
    const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
    
    if (!businessSelectedPackage) return;

    // 加入方案的 deptList 檢項
    businessSelectedPackage.deptList?.forEach((dept: any) => {
      dept.examItemList?.forEach((exam: any) => {
        examItemList.push({
          deptName: dept.deptName,
          examName: exam.examName,
          examItem: exam.examItem,
          examMemo: exam.examMemo || '',
          examPrice: exam.examPrice || '0',
          selectFlag: 1
        });
      });
    });
    console.log('examItemList', examItemList);

    this.sharedStorageService.set('examItemList', examItemList);
  }

  // 是否有補助金額
  get hasSponsorAmount(): boolean {
    return Number(this.sponsorAmt ?? 0) > 0;
  }

  // 檢查企業可預約時間
  private checkBusinessAvailableDates(): void {
    const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
    const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
    const myReservation = this.sharedStorageService.get('myReservation');

    if (!businessPlanRequest || !businessSelectedPackage) return;
    // 更新選擇狀態後，再收集檢項
    const request: BusinessAvailableDatesRequest = {
      orgNo: businessPlanRequest?.orgNo ?? '',
      companyNo: businessPlanRequest?.companyNo ?? '',
      packageNo: businessSelectedPackage.packageNo ?? '',
      gender: businessPlanRequest?.gender ?? '',
      caseNo: myReservation?.caseNo ?? '', // 修改預約時必填
      dayType: businessSelectedPackage.dayType ?? '',
      bookPeriod: businessSelectedPackage.bookPeriod ?? '',
      examItemList: this.buildSimpleExamItemList(),
      addList: [],
    };
    // 查詢有無可預約時間
    this.appointmentTimeService.getBusinessAvailableDates(request).subscribe(res => {
      console.log('res', res);
      if (res.data && res.data.bookCalInfos && res.data.nutCalInfos) {
        const allBookCntZero = res.data.bookCalInfos.every(info => Number(info.bookCnt) === 0);
        console.log('allBookCntZero', allBookCntZero);
        const allNutCntZero = res.data.nutCalInfos.every(info => Number(info.nutBookCnt) === 0);
        console.log('allNutCntZero', allNutCntZero);

        // 若沒有可預約人數
        if (allBookCntZero && allNutCntZero) {
          const dialog = this.dialog.open(SingleActionDialogComponent, {
            data: {
              title: this.translateService.instant('business-reserve-compare-choose-dialog.dialog-title'),
              message: this.translateService.instant('business-reserve-compare-choose-dialog.dialog-message'),
              buttonText: this.translateService.instant('business-reserve-compare-choose-dialog.dialog-reselect'),
              showCloseButton: false,
              action: () => {
                this.sharedStorageService.remove('businessSelectedPackage');
                dialog.close();
                window.location.reload();
              },
            },
          });
        } else {
          // 建立完整的檢項清單並儲存到 sharedStorage
          this.buildAndSaveExamItemList();
          this.router.navigate(['/healthcheck/reserve/additem']).then();
        }
      }
    });
  }
}
